import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Info, ChevronDown, UserPlus, UsersRound } from 'lucide-react';
import { API } from '../config/api';
import { useLanguage } from '../contexts/LanguageContext';

const defaultFaqsGr = [
  { q: 'Τι είναι το WBL;', a: 'Το WBL – Women\'s Basketball League είναι το πρώτο, οργανωμένο, ερασιτεχνικό γυναικείο πρωτάθλημα μπάσκετ στην Αθήνα και γενικά στην Ελλάδα.' },
  { q: 'Ποιοι μπορούν να συμμετέχουν;', a: 'Παίκτριες που παίζουν ερασιτεχνικά, έπαιζαν στο παρελθόν ή ομάδες που θέλουν οργανωμένο πλαίσιο. Δεν απαιτείται δελτίο.' },
  { q: 'Μπορώ να δηλώσω ατομικά;', a: 'Ναι. Υπάρχει ατομική δήλωση για δημιουργία ή συμπλήρωση ομάδων.' },
  { q: 'Μπορούμε να δηλώσουμε ομάδα;', a: 'Ναι. Ομάδες μπορούν να δηλώσουν συμμετοχή με το υπάρχον ρόστερ τους.' },
  { q: 'Τι απαιτείται για να συμμετάσχω;', a: 'Απαιτείται υπογεγραμμένη Δήλωση Συμμετοχής, Δήλωση Ανάληψης Κινδύνου, έγκυρη ιατρική βεβαίωση ή ενεργή κάρτα αθλητή.' },
  { q: 'Πού παίζονται οι αγώνες;', a: 'Σε κλειστά γήπεδα στην Αθήνα. Οι λεπτομέρειες ανακοινώνονται μετά το κλείδωμα συμμετοχών.' },
  { q: 'Τι ώρες παίζουμε;', a: 'Κυρίως απογευματινές / βραδινές ώρες (ενδεικτικά 19:00–22:00).' },
  { q: 'Με ποιους κανονισμούς;', a: 'Με επίσημους κανονισμούς FIBA.' },
  { q: 'Υπάρχει ιατρική κάλυψη;', a: 'Υπάρχει παρουσία γιατρού για την λήψη πρώτων βοηθειών σε κάθε αγώνα. Σε σοβαρό περιστατικό ακολουθείται η προβλεπόμενη διαδικασία (ΕΚΑΒ).' },
  { q: 'Υπάρχει κάποιο έπαθλο για την πρωταθλήτρια ομάδα;', a: 'Ναι. Η πρωταθλήτρια ομάδα του WBLA θα λάβει κύπελλο και αναμνηστικά δώρα ως επιβράβευση της αγωνιστικής της πορείας, όπως επίσης γενικά οι τρεις φιναλίστ θα λάβουν μετάλλια (Χρυσό, Αργυρό, Χάλκινο). Η MVP της σεζόν θα λάβει τιμητικό βραβείο και έξτρα δώρα ως επιβράβευση!' },
  { q: 'Πώς διεξάγεται το πρωτάθλημα;', a: 'Όλες οι ομάδες παίζουν εναντίον όλων στην κανονική περίοδο. Στο τέλος θα διεξαχθεί Final 4 (playoffs).' },
  { q: 'Πότε ξεκινάει;', a: 'Μετά την ολοκλήρωση δηλώσεων και τον σχηματισμό ομάδων.' },
  { q: 'Πώς δηλώνω συμμετοχή;', a: 'Μέσω της ατομικής ή της ομαδικής φόρμας που υπάρχουν στο site μας.' }
];

const defaultFaqsEn = [
  { q: 'What is WBL?', a: 'WBL – Women\'s Basketball League is the first organized amateur women\'s basketball league in Athens and Greece.' },
  { q: 'Who can participate?', a: 'Players who play recreationally, used to play, or teams looking for an organized framework. No official license required.' },
  { q: 'Can I register individually?', a: 'Yes. There is an individual registration form to create or join teams.' },
  { q: 'Can we register as a team?', a: 'Yes. Teams can register with their existing roster.' },
  { q: 'What is required to participate?', a: 'A signed Participation Declaration, Risk Assumption Declaration, and valid medical certificate are required.' },
  { q: 'Where are games played?', a: 'In indoor courts in Athens. Details are announced after registration closes.' },
  { q: 'What time do we play?', a: 'Mostly afternoon/evening hours (approximately 19:00–22:00).' },
  { q: 'What rules apply?', a: 'Official FIBA rules.' },
  { q: 'Is there medical coverage?', a: 'Basic first aid coverage at every game. Enhanced coverage during playoffs.' },
  { q: 'Is there a prize for the champion?', a: 'Yes. The champion team of WBLA will receive a trophy and commemorative gifts as a reward for their competitive performance. Additionally, the top three finalists will receive medals (Gold, Silver, Bronze). The Season MVP will receive an honorary award and extra gifts!' },
  { q: 'How is the league conducted?', a: 'All teams play against each other in regular season. Final 4 playoffs at the end.' },
  { q: 'When does it start?', a: 'After registration closes and teams are formed.' },
  { q: 'How do I register?', a: 'Through the individual or team registration forms on our website.' }
];

const FAQPage = () => {
  const { language } = useLanguage();
  const [openIndex, setOpenIndex] = useState(null);
  const [faqItems, setFaqItems] = useState([]);
  
  useEffect(() => {
    axios.get(`${API}/faq`).then(res => {
      if (res.data.items && res.data.items.length > 0) {
        setFaqItems(res.data.items);
      }
    }).catch(console.error);
  }, []);
  
  // Use dynamic FAQ if available, otherwise fall back to hardcoded
  const faqs = faqItems.length > 0 
    ? faqItems.map(item => ({ q: item.question, a: item.answer }))
    : (language === 'gr' ? defaultFaqsGr : defaultFaqsEn);
  
  return (
    <div className="max-w-4xl mx-auto px-4 py-12" data-testid="faq-page">
      <div className="text-center mb-12">
        <div className="w-16 h-16 rounded-full bg-[#1E3A5F]/10 flex items-center justify-center mx-auto mb-4">
          <Info className="text-[#1E3A5F]" size={32} />
        </div>
        <h1 className="section-title mb-4">{language === 'gr' ? 'Συχνές Ερωτήσεις' : 'FAQ'}</h1>
        <p className="text-slate-600">{language === 'gr' ? 'Απαντήσεις στις πιο συχνές ερωτήσεις για το WBL' : 'Answers to the most common questions about WBL'}</p>
      </div>
      
      <div className="space-y-3">
        {faqs.map((faq, i) => (
          <div key={i} className="card overflow-hidden" data-testid={`faq-${i}`}>
            <button
              onClick={() => setOpenIndex(openIndex === i ? null : i)}
              className="w-full p-5 flex items-center justify-between text-left hover:bg-slate-50 transition"
            >
              <span className="font-semibold text-[#1E3A5F] pr-4">{faq.q}</span>
              <ChevronDown className={`text-[#F97316] transition-transform flex-shrink-0 ${openIndex === i ? 'rotate-180' : ''}`} size={20} />
            </button>
            {openIndex === i && (
              <div className="px-5 pb-5 text-slate-600 border-t border-slate-100 pt-4">
                {faq.a}
              </div>
            )}
          </div>
        ))}
      </div>
      
      {/* Registration Links */}
      <div className="mt-12 bg-gradient-to-r from-[#1E3A5F] to-[#2D5A87] rounded-2xl p-8 text-white text-center">
        <h3 className="font-display font-bold text-2xl mb-4">
          {language === 'gr' ? 'Έτοιμη να ξεκινήσεις;' : 'Ready to start?'}
        </h3>
        <p className="text-white/80 mb-6">
          {language === 'gr' ? 'Δήλωσε συμμετοχή τώρα!' : 'Register now!'}
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a href="https://forms.gle/AdQv3YyPQpbKdRSV6" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#F97316] text-white rounded-full font-semibold hover:bg-[#EA580C] transition">
            <UserPlus size={20} />
            {language === 'gr' ? 'Ατομική Δήλωση' : 'Individual Registration'}
          </a>
          <a href="https://forms.gle/PuZbF8cG82YkzUCi8" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-[#1E3A5F] rounded-full font-semibold hover:bg-slate-100 transition">
            <UsersRound size={20} />
            {language === 'gr' ? 'Δήλωση Ομάδας' : 'Team Registration'}
          </a>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;
